import {default as DocumentScanner} from '@mui/icons-material/DocumentScanner';
import {default as History} from '@mui/icons-material/History';
import {default as Flaky} from '@mui/icons-material/Flaky';
import {default as DynamicFeed} from '@mui/icons-material/DynamicFeed';
import {default as FolderCopy} from '@mui/icons-material/FolderCopy';

//default ActionType icons
import {default as Icon_Download} from '@mui/icons-material/Download';
import {default as Icon_OpenTab} from '@mui/icons-material/OpenInNewRounded';
import {default as Icon_Dialog} from '@mui/icons-material/ViewSidebar';
import {default as Icon_PostRequest} from '@mui/icons-material/Commit';

import {fetchDownloadFile} from "./FileUtils";
import {ReactElement} from 'react';

const actionTypeIconStyle = {
  fontSize: "0.8571rem",
  color: "#19C6F0",
  flexShrink: 0,
};

export const ActionTypes = Object.freeze({
  DOWNLOAD: 'DOWNLOAD',
  OPEN_TAB: 'OPEN_TAB',
  DIALOG: 'DIALOG',
  POST_REQUEST: 'POST_REQUEST',
  HANDLER: 'HANDLER',
} as const);

export type ActionType = typeof ActionTypes[keyof typeof ActionTypes];

export const ActionTypeIcons: Record<ActionType, ReactElement<any>> = Object.freeze({
  [ActionTypes.DOWNLOAD]: <Icon_Download sx={actionTypeIconStyle} aria-hidden="true" />,
  [ActionTypes.OPEN_TAB]: <Icon_OpenTab sx={actionTypeIconStyle} aria-hidden="true" />,
  [ActionTypes.DIALOG]: <Icon_Dialog sx={actionTypeIconStyle} aria-hidden="true" />,
  [ActionTypes.POST_REQUEST]: <Icon_PostRequest sx={actionTypeIconStyle} aria-hidden="true" />,
  //[ActionTypes.HANDLER]: by definition this is a custom action so it should not have a default icon
  [ActionTypes.HANDLER]: <></>,
});

export const ActionDefaultIcons: Record<ActionType, ReactElement<any>> = Object.freeze({
  [ActionTypes.DOWNLOAD]: <DocumentScanner/>,
  [ActionTypes.OPEN_TAB]: <FolderCopy/>,
  [ActionTypes.DIALOG]: <DynamicFeed/>,
  [ActionTypes.POST_REQUEST]: <Flaky/>,
  [ActionTypes.HANDLER]: <History/>,
});

export const CallbackActions = Object.freeze({
  SUCCESS: 'SUCCESS',
  OPEN_DIALOG: 'OPEN_DIALOG',
  ERROR: 'ERROR',
  LOGOUT: 'LOGOUT',
  POST_REQUEST: 'POST_REQUEST',
} as const);

export type CallbackAction = typeof CallbackActions[keyof typeof CallbackActions];
export type OnActionCallback = (action: CallbackAction, instrument: unknown, message?: string) => void;

export interface ActionPayload<T = unknown> {
  endpoint: (instrument: T) => string;
  successMessage?: string;
  errorMessage?: string;
}

export interface actionExecutionParams<T,> {
  instrument: T,
  actionPayload: ActionPayload<T>,
  onAction?: OnActionCallback
}

export const executeDownload = <T = unknown>(
  instrument: T,
  actionPayload: ActionPayload<T>,
  onAction?: OnActionCallback
): void => {  
  const onError = onAction
    ? () => {
      onAction(CallbackActions.ERROR, instrument, actionPayload.errorMessage || '');
      return {};
    }
    : () => ({});

  const onSuccess = onAction
    ? () => {
      onAction(CallbackActions.SUCCESS, instrument, actionPayload.successMessage || '');
      return {};
    }
    : () => ({});

  fetchDownloadFile(
    actionPayload.endpoint(instrument),
    onSuccess,
    onError,
  );
};

export const executeOpenTab = <T = unknown>(
  instrument: T,
  actionPayload: ActionPayload<T>,
  onAction?: OnActionCallback
): void => {
  try {
    window.open(actionPayload.endpoint(instrument));
    if (onAction) {
      onAction(CallbackActions.SUCCESS, instrument, actionPayload.successMessage || '');
    }
  } catch {
    if (onAction) {
      onAction(CallbackActions.ERROR, instrument, actionPayload.errorMessage || '');
    }
  }
};